import type { ChatBotEntity } from '@common/types/chat-bot.type';
import type { ChatBotChainEntity } from '@common/types/chat-bot-chain.type';
import type {
    ChatBotBotChainFormType,
    ChatBotElementFormGroup,
    ChatBotElementFormType,
} from '@web/widgets/chat-bot/type/chat-bot-widget.type';

import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    Input,
    type OnChanges,
    type SimpleChanges,
} from '@angular/core';
import { type Observable, of } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import { MESSENGER_BOT_LINK_BASE, chatBotsUrl } from '@common/constants/chat-bots.consts';
import { EChatBotsMessengers } from '@common/enums/chat-bots.enum';
import { ESocialNetworks } from '@common/enums';
import { isEmptyButtonLabel } from '@common/helpers/forms';

import { ChatBotsCollectionFacade } from '@web/store/collections/chat-bots/chat-bots.facade';
import { ChatBotChainsCollectionFacade } from '@web/store/collections/chat-bot-chains/chat-bot-chains.facade';

import { WebBaseControlTypedSettingsComponent } from '@web/widgets/common/abstract/web-base-control-typed-settings.component';
import { AppFacade } from '@web/store/app/app.facade';
import { CommonElementsModule } from '@web/widgets/common/common-elements.module';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import type { FormArray } from '@angular/forms';
import { type FormGroup } from '@angular/forms';

@Component({
    selector: 'web-chat-bot-settings',
    templateUrl: './chat-bot-settings.component.html',
    styleUrls: ['./chat-bot-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonElementsModule, BsDropdownModule],
})
export class ChatBotSettingsComponent
    extends WebBaseControlTypedSettingsComponent<ChatBotElementFormGroup>
    implements OnChanges
{
    @Input() public chatBotsList: ChatBotEntity[] = [];
    @Input() public isSubscriptionForm: boolean = false;
    @Input() public selectedChatbots: FormArray<FormGroup<ChatBotElementFormType>> = null;
    @Input() set existingChatBots(value: {
        telegram?: 'telegram';
        instagram?: 'instagram';
        viber?: 'viber';
        messenger?: 'messenger';
        whatsapp?: 'whatsapp';
        tiktok?: 'tiktok';
    }) {
        this.localExistingChatBots = structuredClone(value);
    }

    private readonly changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
    private readonly chatBotsCollectionFacade: ChatBotsCollectionFacade = inject(ChatBotsCollectionFacade);
    private readonly chatBotChainsCollectionFacade: ChatBotChainsCollectionFacade =
        inject(ChatBotChainsCollectionFacade);
    private readonly appFacade: AppFacade = inject(AppFacade);

    public readonly MAX_DESCRIPTION_LENGTH = 30;

    private chatBotsMap: Record<string, ChatBotEntity> = {};
    private currentChatBot: ChatBotEntity | null = null;
    private localExistingChatBots: {
        telegram?: 'telegram';
        instagram?: 'instagram';
        viber?: 'viber';
        messenger?: 'messenger';
        whatsapp?: 'whatsapp';
        tiktok?: 'tiktok';
    };

    public isChainsLoading = false;

    protected readonly ESocialNetworks = ESocialNetworks;

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.chatBotsList && this.chatBotsList) {
            this.chatBotsMap = this.chatBotsList.reduce((acc, item) => {
                acc[item.id.toString()] = item;
                return acc;
            }, {});

            if (this.botId.value) {
                const isSelectedBotInList = this.chatBotsList.some((item) => item.id.toString() === this.botId.value);
                if (!isSelectedBotInList) {
                    this.itemForm.patchValue({
                        botId: null,
                        botName: null,
                        botChain: {
                            chainId: null,
                            name: null,
                            isDefault: false,
                        },
                        uri: null,
                        phone: null,
                    });
                    return;
                }

                this.currentChatBot = this.getCurrentChatBot();
            }
        }
    }

    public setMessenger(messengerType: ESocialNetworks): void {
        this.appFacade
            .isAdminCollectionWrapper$(this.chatBotsCollectionFacade.chatBots$(messengerType))
            .pipe(
                filter((chatBots) => !!chatBots),
                take(1)
            )
            .subscribe((chatBots) => {
                this.chatBotsMap = chatBots.reduce((acc, item) => {
                    acc[item.id.toString()] = item;
                    return acc;
                }, {});

                this.socialNetwork.setValue(messengerType, { emitEvent: false });
                this.name.setValue(messengerType, { emitEvent: false });

                if (chatBots.length) {
                    this.currentChatBot = chatBots[0];

                    this.itemForm.patchValue(
                        {
                            botName: this.currentChatBot.name,
                            botId: this.currentChatBot.id.toString(),
                            uri: this.currentChatBot?.uri,
                            phone: this.currentChatBot?.phone,
                        },
                        { emitEvent: false }
                    );
                    this.setDefaultChain(messengerType);
                    return;
                }

                this.itemForm.patchValue({
                    link: null,
                    botName: null,
                    botId: null,
                    botChain: {
                        chainId: null,
                        name: null,
                        isDefault: false,
                    },
                    uri: null,
                    phone: null,
                });

                this.changeDetectorRef.detectChanges();
            });
    }

    public setDefaultChain(messengerType: string): void {
        this.chatBotChainsCollectionFacade
            .chatBotChains$(messengerType, this.currentChatBot.externalId)
            .pipe(
                filter((chains) => Boolean(chains)),
                take(1)
            )
            .subscribe((chains) => {
                const defaultChain = chains.find((chain) => chain.isDefault);

                if (defaultChain) {
                    this.setBotChain(defaultChain);
                } else {
                    this.setBotChain(chains[0]);
                }
            });
    }

    public useOwnLink(): void {
        this.itemForm.patchValue({
            link: null,
            botId: null,
            botName: null,
            botChain: {
                chainId: null,
                name: null,
                isDefault: false,
            },
            uri: null,
            phone: null,
            preventLinkConverting: false,
        });
    }

    public setBot(bot: ChatBotEntity): void {
        this.isChainsLoading = true;
        this.chatBotChainsCollectionFacade
            .chatBotChains$(this.socialNetwork.value, bot.externalId)
            .pipe(
                filter((chatBotChains) => Boolean(chatBotChains)),
                take(1)
            )
            .subscribe((chatBotChains) => {
                this.isChainsLoading = false;
                this.currentChatBot = bot;
                this.botChain.reset();

                this.itemForm.patchValue(
                    {
                        botName: bot.name,
                        botId: bot.id.toString(),
                        uri: bot?.uri,
                        phone: bot?.phone,
                    },
                    { emitEvent: false }
                );

                if (chatBotChains.length) {
                    const defaultChain = chatBotChains.find((chain) => chain.isDefault);

                    if (defaultChain) {
                        this.setBotChain(defaultChain);
                    } else {
                        this.setBotChain(chatBotChains[0]);
                    }

                    this.changeDetectorRef.detectChanges();
                    return;
                }

                this.itemForm.patchValue({
                    botChain: {
                        chainId: null,
                        name: null,
                        isDefault: false,
                    },
                });
                this.changeDetectorRef.detectChanges();
            });
    }

    public setLink(): void {
        const host = chatBotsUrl[this.socialNetwork.value][0];
        const selectedChatBot = this.getCurrentChatBot();

        if (!selectedChatBot) {
            return;
        }

        switch (this.socialNetwork.value) {
            case ESocialNetworks.instagram:
                this.link.setValue(`https://ig.me/m/${this.botId.value}?ref=${this.botChain.value.chainId}`);
                break;
            case ESocialNetworks.messenger:
                // Built on `messenger.com/t`, not the `m.me` shortener Meta is retiring (#73155).
                this.link.setValue(`${MESSENGER_BOT_LINK_BASE}/${this.botId.value}?ref=${this.botChain.value.chainId}`);
                break;
            case ESocialNetworks.telegram:
                this.link.setValue(`${host}/${selectedChatBot.name}?start=${this.botChain.value.chainId}`);
                break;
            case ESocialNetworks.viber:
                this.link.setValue(`viber://pa?chatURI=${this.uri.value}&context=${this.botChain.value.chainId}`);
                break;
            case ESocialNetworks.whatsapp:
                this.link.setValue(`${host}/${this.phone.value}?start=${this.botChain.value.chainId}&text=Start`);
                break;
            case ESocialNetworks.tiktok:
                this.link.setValue(`${host}/${selectedChatBot.name.toLowerCase()}?ref=${this.botChain.value.chainId}`);
                break;
            default:
                this.link.setValue('');
        }
    }

    public getCurrentChatBot(): ChatBotEntity | null {
        if (this.botId.value && this.chatBotsMap[this.botId.value.toString()]) {
            return this.chatBotsMap[this.botId.value.toString()];
        }

        return null;
    }

    public setBotChain(value: ChatBotChainEntity): void {
        this.botChain.setValue(value);
        this.setLink();
    }

    public getPlaceholder(social: string): string {
        switch (social) {
            case EChatBotsMessengers.email:
                return 'example@mail.com';
            case EChatBotsMessengers.phone:
                return '+1 234 567 89 10';
            default:
                return 'https://site.com';
        }
    }

    public get socialNetwork(): ChatBotElementFormType['socialNetwork'] {
        return this.itemForm.get('socialNetwork') as ChatBotElementFormType['socialNetwork'];
    }

    public get iconType(): ChatBotElementFormType['iconType'] {
        return this.itemForm.get('iconType') as ChatBotElementFormType['iconType'];
    }

    public get description(): ChatBotElementFormType['description'] {
        return this.itemForm.get('description') as ChatBotElementFormType['description'];
    }

    public get link(): ChatBotElementFormType['link'] {
        return this.itemForm.get('link') as ChatBotElementFormType['link'];
    }

    public get name(): ChatBotElementFormType['name'] {
        return this.itemForm.get('name') as ChatBotElementFormType['name'];
    }

    /** The name doubles as the icon's title — blank leaves the link unlabelled. */
    public get saveDisablePredicate(): boolean {
        return isEmptyButtonLabel(this.name?.value);
    }

    public get botName(): ChatBotElementFormType['botName'] {
        return this.itemForm.get('botName') as ChatBotElementFormType['botName'];
    }

    public get botId(): ChatBotElementFormType['botId'] {
        return this.itemForm.get('botId') as ChatBotElementFormType['botId'];
    }

    public get uri(): ChatBotElementFormType['uri'] {
        return this.itemForm.get('uri') as ChatBotElementFormType['uri'];
    }

    public get phone(): ChatBotElementFormType['phone'] {
        return this.itemForm.get('phone') as ChatBotElementFormType['phone'];
    }

    public get botChain(): ChatBotElementFormType['botChain'] {
        return this.itemForm.get('botChain') as ChatBotElementFormType['botChain'];
    }

    public get preventLinkConverting(): ChatBotElementFormType['preventLinkConverting'] {
        return this.itemForm.get('preventLinkConverting') as ChatBotElementFormType['preventLinkConverting'];
    }

    public get chainId(): ChatBotBotChainFormType['chainId'] {
        return this.botChain.get('chainId') as ChatBotBotChainFormType['chainId'];
    }

    public get chainName(): ChatBotBotChainFormType['name'] {
        return this.botChain.get('name') as ChatBotBotChainFormType['name'];
    }

    public get messengerNames(): ESocialNetworks[] {
        const forbiddenMessengers = this.isSubscriptionForm
            ? [EChatBotsMessengers.vk, EChatBotsMessengers.ok, EChatBotsMessengers.tiktok]
            : [EChatBotsMessengers.vk, EChatBotsMessengers.ok];

        const sourceKeys = this.isSubscriptionForm
            ? Object.keys(this.localExistingChatBots)
            : Object.keys(EChatBotsMessengers);

        const selectedNetworks = new Set(
            this.selectedChatbots?.value
                .map((item) => item.socialNetwork)
                .filter((network) => network !== this.socialNetwork.value) ?? []
        );

        return sourceKeys.filter(
            (key) =>
                !forbiddenMessengers.includes(key as EChatBotsMessengers) &&
                !selectedNetworks.has(key as ESocialNetworks)
        ) as ESocialNetworks[];
    }

    public get chatBotChains$(): Observable<ChatBotChainEntity[]> {
        if (!this.socialNetwork.value || !this.botId.value) {
            return of([]);
        }

        const selectedChatBot = this.getCurrentChatBot();
        if (!selectedChatBot) {
            return of([]);
        }

        const messenger = this.socialNetwork.value;
        return this.chatBotChainsCollectionFacade.chatBotChains$(messenger, selectedChatBot.externalId);
    }
}
